import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, FileSearch, LayoutDashboard } from 'lucide-react';
import { BrandIcon, Spinner, SubTabs, TabPanel } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { SearchConsoleCard } from '@/components/sites/SearchConsoleCard';
import { SeoTimelineCard } from '@/components/sites/SeoTimelineCard';
import { SeoMetricsOverview } from '@/components/sites/seo/SeoMetricsOverview';
import { SeoSummary, type SeoSection } from '@/components/sites/seo/SeoSummary';
import { SeoTechnical } from '@/components/sites/seo/SeoTechnical';
import { useRequestSeoCheck, useSiteSeo } from '@/hooks/useSeo';
import { useSiteSearch } from '@/hooks/useSearchConsole';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import type { SiteDetail } from '@/types/api';

const SECTIONS: readonly SeoSection[] = ['summary', 'search', 'metrics', 'technical'];

/**
 * What a site says to search engines (ADR-0045), on four tabs (ADR-0057): the
 * summary with the figures that matter and what is wrong; Google Search
 * Console — connecting it, its queries, pages and days; the domain as the link
 * graph sees it, the way Ahrefs's overview shows it; and the technical read —
 * the front page, robots.txt, the sitemap, the pages the operator watches.
 * The tab is in the address, so a link opens the right one.
 */
export function SeoTab({ site, mayEdit }: { readonly site: SiteDetail; readonly mayEdit: boolean }): ReactNode {
  const seo = useSiteSeo(site.id);
  const search = useSiteSearch(site.id);
  const [params, setParams] = useSearchParams();
  const requested = params.get('seo');
  const section: SeoSection = SECTIONS.includes(requested as SeoSection) ? (requested as SeoSection) : 'summary';
  const open = (next: SeoSection) => {
    const copy = new URLSearchParams(params);
    if (next === 'summary') copy.delete('seo');
    else copy.set('seo', next);
    setParams(copy, { replace: true });
  };
  const check = useRequestSeoCheck(site.id, {
    onSuccess: () => toast.success('Проверка поставлена в очередь'),
    onError: (error) => toast.error('Не удалось запросить проверку', errorText(error)),
  });

  if (seo.isPending) return <Spinner />;
  if (seo.isError) return <ErrorState error={seo.error} />;
  const view = seo.data;
  const problems = view.issues.filter((issue) => issue.severity !== 'info').length;

  return (
    <div className="stack">
      <SubTabs
        label="Разделы SEO"
        prefix="seo-"
        active={section}
        onChange={open}
        tabs={[
          { id: 'summary', label: 'Сводка', icon: LayoutDashboard, ...(problems > 0 ? { count: problems } : {}) },
          { id: 'search', label: 'Search Console', mark: <BrandIcon name="googlesearchconsole" size={14} /> },
          { id: 'metrics', label: 'Показатели', icon: BarChart3, disabled: !site.primaryDomain },
          { id: 'technical', label: 'Техническое', icon: FileSearch },
        ]}
      />

      <TabPanel id="summary" active={section} prefix="seo-">
        <SeoSummary site={site} view={view} mayEdit={mayEdit} checking={check.isPending} onCheck={() => check.mutate()} onOpen={open} />
      </TabPanel>

      <TabPanel id="search" active={section} prefix="seo-">
        <div className="stack">
          <SearchConsoleCard site={site} />
          {site.primaryDomain && search.data?.state === 'verified' ? <SeoTimelineCard siteId={site.id} metrics={['clicks', 'impressions', 'position']} title="Клики, показы и позиция по дням" /> : null}
        </div>
      </TabPanel>

      <TabPanel id="metrics" active={section} prefix="seo-">
        {site.primaryDomain ? <SeoMetricsOverview siteId={site.id} /> : null}
      </TabPanel>

      <TabPanel id="technical" active={section} prefix="seo-">
        <SeoTechnical site={site} view={view} mayEdit={mayEdit} />
      </TabPanel>
    </div>
  );
}

